import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Check, RefreshCw, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../App';

const API = '/api';

const UserInbox = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const limit = 20;

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/messages/inbox`, {
        params: { page, limit },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMessages(response.data.messages || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching inbox:', error);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    fetchMessages();
  }, [user, authLoading, navigate, fetchMessages]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMessages();
    setRefreshing(false);
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.post(`${API}/api/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true, read_at: new Date().toISOString() } : m
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      await handleMarkRead(message.id);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="user-inbox">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-green-500" />
                <h1 className="text-xl font-bold">Inbox</h1>
              </div>
              {total > 0 && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                  {total} message{total !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {messages.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <h2 className="text-xl font-semibold text-zinc-400 mb-2">No messages yet</h2>
            <p className="text-zinc-500">Messages from the Trap Comix team will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => (
              <div
                key={message.id}
                onClick={() => openMessage(message)}
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:border-zinc-600 ${
                  message.is_read 
                    ? 'bg-zinc-900/30 border-zinc-800' 
                    : 'bg-zinc-900 border-zinc-700'
                }`}
                data-testid={`message-${message.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!message.is_read && (
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                      )}
                      <h3 className={`font-semibold truncate ${message.is_read ? 'text-zinc-400' : 'text-white'}`}>
                        {message.subject}
                      </h3>
                    </div>
                    <p className="text-sm text-zinc-500 truncate">{message.body}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-zinc-500">{formatDate(message.created_at)}</span>
                    {message.is_read && <Check className="w-4 h-4 text-zinc-600" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.max(1, p - 1))} 
              disabled={page === 1}
              className="border-zinc-700"
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-400">Page {page} of {totalPages}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
              disabled={page === totalPages}
              className="border-zinc-700"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMessage(null)}
        >
          <div 
            className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">{selectedMessage.subject}</h2>
                  <p className="text-sm text-zinc-500">From: {selectedMessage.sender_name}</p>
                </div>
                <span className="text-xs text-zinc-500">{formatDate(selectedMessage.created_at)}</span>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              <p className="text-zinc-300 whitespace-pre-wrap">{selectedMessage.body}</p>
            </div>
            <div className="p-4 border-t border-zinc-800">
              <Button 
                variant="outline" 
                className="w-full border-zinc-700"
                onClick={() => setSelectedMessage(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInbox;
